/**
 * Sentry telemetry for the PlateEditor page-break model + server PDF export
 * (RECUR-27304 family). Pure OBSERVABILITY — nothing here may ever change
 * editor or export behaviour, so every reporting entry point is wrapped in
 * its own try/catch and silently no-ops on failure (including when Sentry
 * isn't initialised at all — `@sentry/react` v8 no-ops safely outside prod,
 * mirroring `src/index.tsx`'s prod-only `Sentry.init`).
 *
 * What it watches, and why (each maps to a real past incident class):
 *
 *  1. INVARIANT: "no planned page may exceed the physical page"
 *     (`checkPlannedPageSpans`). When a planned span is taller than
 *     `pageHeightPx`, headless Chromium inserts an UNPLANNED cut at render
 *     time and the exported PDF desyncs from the editor overlay — the exact
 *     failure the reverted EXTEND-bias stopgap (#3419) shipped, and what the
 *     fit-loop's EXTEND branch still produces for taller-than-page atomic
 *     units (giant images, unsegmentable rows, rowspan groups).
 *
 *  2. OSCILLATION: the boundary key flip-flopping between a small set of
 *     values in a tight window (`createBoundaryThrashDetector`) — the
 *     signature of a measure→gap-paint→reflow→measure feedback loop, i.e.
 *     the historical "page started to glitch" bug (seam/chip jitter,
 *     RECUR-27304-overlay-scroll-jitter). Ordinary typing produces a
 *     PROGRESSION of distinct keys and never trips this.
 *
 *  3. EXPORT OUTCOME: a server-PDF failure used to silently fall back to
 *     the raster exporter with only a console.error — the fallback is now
 *     REMOVED (RECUR-26973) and `reportPdfServerExportFailure` makes every
 *     failure visible so the server path gets fixed. `reportPageCountDesyncIfAny` compares the model's
 *     planned page count against the page count parsed out of the returned
 *     PDF bytes, catching plan/render desync in production (SEDNA repro:
 *     planned 3 pages, rendered 4).
 *
 * Event taxonomy (for Sentry alert rules): every event carries the tag
 * `plate_editor` (`pagination` | `pdf-export`) plus a per-check tag and a
 * stable fingerprint, so each failure class groups into ONE issue.
 */
import * as Sentry from '@sentry/react';
import { computePageStartUnits, getTopLevelBlocks, type PageBoundaries } from './pageStartUnits';

// A planned span must beat the page height by more than this before it is
// reported — knife-edge font-rendering noise flips boundaries by ±1-2px
// (see PR #3421's problem statement) and must not page anyone.
const OVERFLOW_REPORT_THRESHOLD_PX = 4;

// Oscillation detection: >= MIN_REPEATS sightings of the SAME boundary key,
// interleaved with at least one other key, among >= MIN_SAMPLES key CHANGES
// inside WINDOW_MS. The hook only records key CHANGES, so repeats are never
// consecutive — a repeat inherently means A→…→A flip-flopping.
const THRASH_WINDOW_MS = 3_000;
const THRASH_MIN_REPEATS = 3;
const THRASH_MIN_SAMPLES = 6;
const THRASH_BUFFER_MAX_ENTRIES = 40;

// Per-pageload event caps, so a pathological document can't burn quota.
const MAX_INVARIANT_EVENTS = 5;
const MAX_THRASH_EVENTS = 2;
const MAX_EXPORT_EVENTS = 10;

// PDFs above this size are not scanned for a page count (the byte scan
// decodes the whole blob; exports are typically well under this).
const MAX_PDF_SCAN_BYTES = 25 * 1024 * 1024;

// How many boundary Ys to attach to events/context — enough for any real
// document, bounded so a runaway plan can't bloat the payload.
const MAX_BOUNDARIES_IN_CONTEXT = 40;

export interface PaginationInvariantViolation {
    check: 'page-span-overflow' | 'last-page-overflow' | 'non-monotonic-boundaries' | 'shape-mismatch';
    /** 1-based page number the violation belongs to (-1 for shape-mismatch). */
    pageIndex: number;
    spanPx?: number;
    overflowPx?: number;
}

/**
 * Pure validator for a page-break model result, in the model's own GAP-FREE
 * coordinates (pass `computePageBoundaries`' result BEFORE the hook maps it
 * to live coordinates). `normalizedContentBottomPx` — the gap-free bottom of
 * the last top-level block (see `measureNormalizedContentBottomPx`) — bounds
 * the LAST page, which has no following boundary; pass null to skip it.
 */
export function checkPlannedPageSpans(decision: PageBoundaries, normalizedContentBottomPx: number | null): PaginationInvariantViolation[] {
    const { boundaries, kinds, gaps, pageHeightPx } = decision;
    if (!Number.isFinite(pageHeightPx) || pageHeightPx <= 0) return [];

    if (kinds.length !== boundaries.length || gaps.length !== boundaries.length) {
        return [{ check: 'shape-mismatch', pageIndex: -1 }];
    }

    const violations: PaginationInvariantViolation[] = [];
    const pageTops = [0, ...boundaries];
    pageTops.forEach((top, i) => {
        const nextBoundary = i + 1 < pageTops.length ? pageTops[i + 1] : null;
        if (nextBoundary != null && nextBoundary <= top) {
            violations.push({ check: 'non-monotonic-boundaries', pageIndex: i + 1 });
            return;
        }
        const pageEnd = nextBoundary ?? normalizedContentBottomPx;
        if (pageEnd == null) return; // last page, bottom unknown — nothing to check
        const spanPx = pageEnd - top;
        const overflowPx = spanPx - pageHeightPx;
        if (overflowPx > OVERFLOW_REPORT_THRESHOLD_PX) {
            violations.push({
                check: nextBoundary == null ? 'last-page-overflow' : 'page-span-overflow',
                pageIndex: i + 1,
                spanPx: Math.round(spanPx),
                overflowPx: Math.round(overflowPx),
            });
        }
    });
    return violations;
}

/**
 * The gap-free bottom edge of the document's LAST top-level block, relative
 * to the content root — rect-based so nested offset-parent chains don't
 * matter. MUST be measured while the page-gap styles are STRIPPED (i.e.
 * between `stripPageGapStyles` and `applyPageGapStyles` in the hook's
 * measurement pass) so it shares the model's gap-free coordinate space.
 */
export function measureNormalizedContentBottomPx(contentEl: HTMLElement, blocks: HTMLElement[]): number | null {
    try {
        const last = blocks[blocks.length - 1];
        if (!last) return null;
        return last.getBoundingClientRect().bottom - contentEl.getBoundingClientRect().top;
    } catch {
        return null;
    }
}

interface ThrashHit {
    occurrences: number;
    distinctKeys: number;
    samples: number;
}

/**
 * Sliding-window oscillation detector over boundary-key CHANGES (the caller
 * must only record actual changes — the hook's existing change guard does
 * this). Returns a hit exactly when the same key re-appears
 * `THRASH_MIN_REPEATS`+ times among `THRASH_MIN_SAMPLES`+ recent changes —
 * i.e. the overlay is flip-flopping between layouts, not progressing through
 * an edit. The buffer resets on a hit so one sustained loop reports once.
 * `now` is injectable for tests.
 */
export function createBoundaryThrashDetector(now: () => number = () => Date.now()) {
    let recent: Array<{ key: string; t: number }> = [];
    return {
        record(key: string): ThrashHit | null {
            const t = now();
            recent.push({ key, t });
            recent = recent.filter((e) => t - e.t <= THRASH_WINDOW_MS).slice(-THRASH_BUFFER_MAX_ENTRIES);
            const occurrences = recent.filter((e) => e.key === key).length;
            const distinctKeys = new Set(recent.map((e) => e.key)).size;
            if (recent.length >= THRASH_MIN_SAMPLES && occurrences >= THRASH_MIN_REPEATS && distinctKeys >= 2) {
                const hit = { occurrences, distinctKeys, samples: recent.length };
                recent = [];
                return hit;
            }
            return null;
        },
    };
}

// ── Module session state (per pageload) ─────────────────────────────────────
let invariantEventsSent = 0;
let thrashEventsSent = 0;
let exportEventsSent = 0;
let reportedInvariantSignatures = new Set<string>();
let boundaryThrashDetector = createBoundaryThrashDetector();

/** Test-only: resets caps, dedupe state, and the thrash detector. */
export function __resetPlatePaginationTelemetryForTests(): void {
    invariantEventsSent = 0;
    thrashEventsSent = 0;
    exportEventsSent = 0;
    reportedInvariantSignatures = new Set<string>();
    boundaryThrashDetector = createBoundaryThrashDetector();
}

function paginationContextOf(decision: PageBoundaries, blockCount: number): Record<string, unknown> {
    return {
        pages: decision.boundaries.length + 1,
        pageHeightPx: Math.round(decision.pageHeightPx),
        boundaries: decision.boundaries.slice(0, MAX_BOUNDARIES_IN_CONTEXT).map((b) => Math.round(b)),
        kinds: decision.kinds.slice(0, MAX_BOUNDARIES_IN_CONTEXT).join(','),
        blockCount,
    };
}

/**
 * The hook-side entry point — called by `usePageBoundaries` once per ACCEPTED
 * measurement (i.e. when the boundary key actually changed). Refreshes the
 * `plate_pagination` context (so ANY later event — editor crash, export
 * failure — carries the latest page plan), checks the planned-page
 * invariants, and feeds the oscillation detector. Never throws.
 */
export function reportPaginationMeasurement(args: {
    decision: PageBoundaries;
    normalizedContentBottomPx: number | null;
    blockCount: number;
    boundaryKey: string;
}): void {
    try {
        const { decision, normalizedContentBottomPx, blockCount, boundaryKey } = args;
        const context = paginationContextOf(decision, blockCount);
        Sentry.setContext('plate_pagination', context);

        checkPlannedPageSpans(decision, normalizedContentBottomPx).forEach((violation) => {
            const signature = `${violation.check}:${violation.pageIndex}`;
            if (reportedInvariantSignatures.has(signature) || invariantEventsSent >= MAX_INVARIANT_EVENTS) return;
            reportedInvariantSignatures.add(signature);
            invariantEventsSent++;
            Sentry.withScope((scope) => {
                scope.setTag('plate_editor', 'pagination');
                scope.setTag('plate_pagination_check', violation.check);
                scope.setFingerprint(['plate-pagination', violation.check]);
                scope.setLevel('warning');
                scope.setContext('plate_pagination_violation', { ...violation, ...context });
                Sentry.captureMessage(`plate-pagination: ${violation.check}`);
            });
        });

        const hit = boundaryThrashDetector.record(boundaryKey);
        if (hit && thrashEventsSent < MAX_THRASH_EVENTS) {
            thrashEventsSent++;
            Sentry.withScope((scope) => {
                scope.setTag('plate_editor', 'pagination');
                scope.setTag('plate_pagination_check', 'boundary-thrash');
                scope.setFingerprint(['plate-pagination', 'boundary-thrash']);
                scope.setLevel('warning');
                scope.setContext('plate_pagination_thrash', { ...hit, ...context });
                Sentry.captureMessage('plate-pagination: boundary oscillation (overlay thrash)');
            });
        }
    } catch {
        // Telemetry must never affect the editor.
    }
}

export interface ExportPlanSnapshot {
    plannedPages: number;
    kindCounts: Record<string, number>;
    blockCount: number;
    tableCount: number;
}

/**
 * Snapshots the page-break model's plan for one export — page count and the
 * per-kind breakdown of planned starts — plus cheap document stats. Called
 * AFTER `waitForImages` and immediately before `buildSelfContainedHtml`, so
 * it measures the same stable layout the export pipeline itself measures.
 * Returns null (and reports nothing) on any failure.
 *
 * `reservedPageTopPx` (RECUR-27604, default 0) must be the SAME logo-band
 * reservation the export itself passes down to `computePageStartUnits` —
 * otherwise the plan would count pages against a taller capacity than the
 * render uses, and the desync detector would misfire on every logo export.
 *
 * `capacityScale` (RECUR-27726, default 1) lets a caller re-derive the plan at
 * a tightened capacity — e.g. re-snapshotting AFTER a successful self-
 * correcting retry, so the desync check compares the final blob against the
 * plan that actually produced it, not the original unscaled one.
 */
export function snapshotExportPlan(contentEl: HTMLElement, reservedPageTopPx = 0, capacityScale = 1): ExportPlanSnapshot | null {
    try {
        const units = computePageStartUnits(contentEl, reservedPageTopPx, capacityScale);
        const kindCounts: Record<string, number> = {};
        units.forEach((unit) => {
            kindCounts[unit.kind] = (kindCounts[unit.kind] ?? 0) + 1;
        });
        const snapshot: ExportPlanSnapshot = {
            plannedPages: units.length + 1,
            kindCounts,
            blockCount: getTopLevelBlocks(contentEl).length,
            tableCount: contentEl.querySelectorAll('table').length,
        };
        addPdfExportBreadcrumb('page-break plan snapshot', snapshot as unknown as Record<string, unknown>);
        return snapshot;
    } catch {
        return null;
    }
}

/** Breadcrumb on the shared trail — attaches to ANY later Sentry event. */
export function addPdfExportBreadcrumb(message: string, data?: Record<string, unknown>): void {
    try {
        Sentry.addBreadcrumb({ category: 'plate.pdf.export', level: 'info', message, data });
    } catch {
        // Telemetry must never affect the export.
    }
}

/**
 * The server-PDF pipeline failed. There is NO raster fallback any more — the
 * export surfaces the failure to the user (RECUR-26973: the raster path
 * produced PDFs whose page boundaries diverged from the previewed model, so a
 * silent fallback traded an honest error for a wrong document). This capture
 * is what makes every server failure visible in Sentry so the server path
 * gets fixed instead. No document content and no filename is attached
 * (filenames carry company names).
 */
export function reportPdfServerExportFailure(err: unknown, plan: ExportPlanSnapshot | null): void {
    try {
        if (exportEventsSent >= MAX_EXPORT_EVENTS) return;
        exportEventsSent++;
        const e = err as { response?: { status?: number }; code?: string };
        Sentry.withScope((scope) => {
            scope.setTag('plate_editor', 'pdf-export');
            scope.setTag('plate_pdf_export', 'server-export-failed');
            scope.setFingerprint(['plate-pdf-export', 'server-export-failed']);
            scope.setContext('plate_pdf_export', {
                httpStatus: e?.response?.status ?? null,
                code: e?.code ?? null,
                ...(plan ?? {}),
            });
            Sentry.captureException(err instanceof Error ? err : new Error(String(err)));
        });
    } catch {
        // Telemetry must never affect the export.
    }
}

/**
 * Best-effort page count of a rendered PDF, parsed straight from the bytes
 * (no PDF library): counts `/Type /Page` object markers and takes the page
 * tree's `/Count N`, whichever is larger. Chromium's print PDFs (what the
 * render Lambda produces) keep both visible in plain bytes. Returns null
 * whenever the bytes can't be read confidently — callers must treat null as
 * "unknown", never "zero".
 */
export async function estimatePdfPageCount(blob: Blob): Promise<number | null> {
    try {
        if (blob.size === 0 || blob.size > MAX_PDF_SCAN_BYTES) return null;
        // Manual latin1 decode (byte -> char code 1:1) — TextDecoder's
        // non-UTF encodings aren't available in every runtime this code and
        // its tests run in (jsdom/small-ICU Node), and latin1 is exactly
        // "each byte is its own char".
        const bytes = new Uint8Array(await blob.arrayBuffer());
        let text = '';
        const DECODE_CHUNK_BYTES = 0x8000;
        for (let i = 0; i < bytes.length; i += DECODE_CHUNK_BYTES) {
            text += String.fromCharCode(...Array.from(bytes.subarray(i, i + DECODE_CHUNK_BYTES)));
        }
        if (!text.startsWith('%PDF-')) return null;
        const typePageMatches = text.match(/\/Type\s*\/Page(?![a-zA-Z])/g)?.length ?? 0;
        let maxTreeCount = 0;
        const countRe = /\/Count\s+(\d+)/g;
        for (let m = countRe.exec(text); m; m = countRe.exec(text)) {
            maxTreeCount = Math.max(maxTreeCount, parseInt(m[1], 10));
        }
        const estimate = Math.max(typePageMatches, maxTreeCount);
        return estimate > 0 ? estimate : null;
    } catch {
        return null;
    }
}

/** Which render produced the blob being checked, for a self-correcting-retry export (RECUR-27726) — see `retryPdfRenderOnDesync`. */
export interface PdfRenderRetryContext {
    attempt: 1 | 2;
    capacityScale?: number;
}

/**
 * Compares the model's planned page count against the page count parsed from
 * the returned PDF — a mismatch is the plan/render desync bug class (editor
 * overlay promises N pages, the PDF ships with more). Reports at most once
 * per export; never throws.
 *
 * `retryContext` (RECUR-27726) enriches the report with which render
 * produced `blob` — `attempt: 2` means the one-shot capacity retry already
 * ran and this is STILL a desync (a tighter capacity is strictly closer, but
 * not guaranteed to fully resolve it).
 *
 * `knownActualPages` lets a caller that already decoded `blob` (e.g.
 * `retryPdfRenderOnDesync`, deciding whether to retry) pass that count
 * straight through instead of decoding the same bytes again — trailing and
 * optional so existing direct callers are unaffected. `undefined` means
 * "not yet known"; decodes as before.
 */
export async function reportPageCountDesyncIfAny(
    blob: Blob,
    plan: ExportPlanSnapshot | null,
    retryContext?: PdfRenderRetryContext,
    knownActualPages?: number | null,
): Promise<void> {
    try {
        if (!plan) return;
        const actualPages = knownActualPages !== undefined ? knownActualPages : await estimatePdfPageCount(blob);
        addPdfExportBreadcrumb('pdf received', { bytes: blob.size, actualPages, plannedPages: plan.plannedPages, ...retryContext });
        if (actualPages == null || actualPages === plan.plannedPages) return;
        if (exportEventsSent >= MAX_EXPORT_EVENTS) return;
        exportEventsSent++;
        Sentry.withScope((scope) => {
            scope.setTag('plate_editor', 'pdf-export');
            scope.setTag('plate_pdf_export', 'page-count-desync');
            scope.setFingerprint(['plate-pdf-export', 'page-count-desync']);
            scope.setLevel('warning');
            scope.setContext('plate_pdf_export', { ...plan, actualPages, pdfBytes: blob.size, ...retryContext });
            Sentry.captureMessage('plate-pdf-export: rendered page count desynced from planned pages');
        });
    } catch {
        // Telemetry must never affect the export.
    }
}
